// ShareCard.js — Shareable achievement card modal for NeevStar
import React, { useState, useEffect } from 'react';

const css = `
@keyframes sc2-popIn {
  0%   { transform: scale(0.3) rotate(-6deg); opacity: 0; }
  65%  { transform: scale(1.05) rotate(1deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}
@keyframes sc2-shimmer {
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes sc2-float {
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
@keyframes sc2-glow {
  0%,100% { box-shadow: 0 0 20px rgba(255,214,0,0.4), 0 8px 32px rgba(0,0,0,0.5); }
  50%      { box-shadow: 0 0 48px rgba(255,214,0,0.9), 0 12px 40px rgba(0,0,0,0.6); }
}
@keyframes sc2-confetti {
  0%   { transform: translateY(0) rotate(0); opacity: 1; }
  100% { transform: translateY(80px) rotate(360deg); opacity: 0; }
}
.sc2-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.9);
  z-index: 9992;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Nunito', sans-serif;
}
.sc2-modal {
  width: 100%; max-width: 380px;
  animation: sc2-popIn 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards;
}
.sc2-card {
  background: linear-gradient(160deg, #0A0A2E 0%, #1A1A5E 50%, #0D1B4B 100%);
  border: 2.5px solid rgba(255,214,0,0.6);
  border-radius: 24px;
  padding: 24px 20px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: sc2-glow 2.5s ease-in-out infinite;
}
.sc2-shimmer-line {
  background: linear-gradient(90deg, transparent 0%, rgba(255,214,0,0.3) 50%, transparent 100%);
  background-size: 200% 100%;
  animation: sc2-shimmer 2s linear infinite;
  height: 1px;
  margin: 10px 0;
}
.sc2-bot {
  animation: sc2-float 2.5s ease-in-out infinite;
  display: inline-block;
}
.sc2-confetti-p {
  position: absolute; border-radius: 2px;
  animation: sc2-confetti 1.2s ease-in forwards;
}
.sc2-share-btn {
  border: none; border-radius: 14px;
  padding: 13px 20px;
  font-size: 15px; font-weight: 900;
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  width: 100%;
  transition: transform 0.1s, box-shadow 0.1s;
}
.sc2-share-btn:active { transform: scale(0.97); }
`;

const CONFETTI_COLORS = ['#FFD600','#FF2D55','#00C851','#0066FF','#8B2BE2','#FF9500','#00BCD4'];

// Achievement type configs
const ACHIEVEMENT_CONFIGS = {
  chest:       { bg: 'linear-gradient(135deg,#FF6B00,#FFD600)', icon: '📦', prefix: 'Chest Unlocked!' },
  certificate: { bg: 'linear-gradient(135deg,#0066FF,#8B2BE2)', icon: '🎓', prefix: 'Certificate Earned!' },
  streak:      { bg: 'linear-gradient(135deg,#FF2D55,#FF6B00)', icon: '🔥', prefix: 'Streak Milestone!' },
  legendary:   { bg: 'linear-gradient(135deg,#FFD600,#FF9500)', icon: '👑', prefix: 'Legendary Win!' },
  level:       { bg: 'linear-gradient(135deg,#8B2BE2,#0066FF)', icon: '⬆️', prefix: 'Level Up!' },
  badge:       { bg: 'linear-gradient(135deg,#00A86B,#00C851)', icon: '🏅', prefix: 'Badge Earned!' },
};

// Global show trigger
let showFn = null;
export function showShareCard(type, achievementText, childName, coinAmount) {
  showFn && showFn({ type, achievementText, childName, coinAmount });
}

export function ShareCardProvider({ children, profile, coins }) {
  const [card, setCard] = useState(null);
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    showFn = (data) => {
      setCard(data);
      setConfetti(Array.from({ length: 22 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${5 + Math.random() * 50}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${Math.random() * 0.5}s`,
        size: 5 + Math.random() * 8,
      })));
    };
    return () => { showFn = null; };
  }, []);

  if (!card) return children;

  const cfg = ACHIEVEMENT_CONFIGS[card.type] || ACHIEVEMENT_CONFIGS.badge;
  const childName = card.childName || profile?.child_name || 'Champion';
  const classLevel = profile?.class_level || '';

  const waText = `🌟 ${childName} just achieved something amazing on NeevStar!\n\n${cfg.icon} ${cfg.prefix}\n"${card.achievementText}"\n\n${card.coinAmount ? `🪙 +${card.coinAmount} NeevCoins earned!\n\n` : ''}Class ${classLevel} • Join at neevstar.com`;

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, '_blank');
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(waText).catch(() => {});
  };

  return (
    <>
      {children}
      <div className="sc2-overlay">
        <div className="sc2-modal">
          {/* The shareable card */}
          <div className="sc2-card" id="neevstar-share-card">

            {/* Confetti */}
            {confetti.map(c => (
              <div key={c.id} className="sc2-confetti-p"
                style={{ left: c.left, top: c.top, background: c.color, width: c.size, height: c.size, animationDelay: c.delay }} />
            ))}

            {/* Top logo row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 18 }}>⭐</span>
                <span style={{ fontSize: 14, fontWeight: 900, color: 'rgba(255,255,255,0.9)' }}>NeevStar</span>
              </div>
              <div style={{
                background: cfg.bg,
                borderRadius: 10, padding: '3px 10px',
                fontSize: 11, fontWeight: 900, color: 'white',
              }}>
                {cfg.prefix}
              </div>
            </div>

            <div className="sc2-shimmer-line" />

            {/* Bot */}
            <div className="sc2-bot" style={{ fontSize: 56, margin: '8px 0' }}>
              {cfg.icon}
            </div>

            {/* Achievement */}
            <div style={{
              background: 'rgba(255,214,0,0.12)',
              border: '1.5px solid rgba(255,214,0,0.4)',
              borderRadius: 16, padding: '10px 16px', margin: '8px 0',
            }}>
              <p style={{ fontSize: 16, fontWeight: 900, color: '#FFD600', margin: '0 0 2px' }}>
                {card.achievementText}
              </p>
              {card.coinAmount && (
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 700, margin: 0 }}>
                  🪙 +{card.coinAmount} NeevCoins earned!
                </p>
              )}
            </div>

            {/* Child name */}
            <p style={{ fontSize: 18, fontWeight: 900, color: 'white', margin: '8px 0 2px' }}>
              🌟 {childName}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, margin: '0 0 8px' }}>
              Class {classLevel} • neevstar.com
            </p>

            <div className="sc2-shimmer-line" />

            {/* Coins display */}
            {coins > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, margin: '8px 0' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 18, fontWeight: 900, color: '#FFD600', margin: 0 }}>🪙 {coins}</p>
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontWeight: 700, margin: 0, letterSpacing: '0.06em' }}>NEEVCOINS</p>
                </div>
              </div>
            )}
          </div>

          {/* Share buttons */}
          <div style={{ padding: '12px 0 0', display: 'grid', gap: 8 }}>
            <button className="sc2-share-btn" onClick={handleWhatsApp}
              style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)', color: 'white', boxShadow: '0 4px 16px rgba(37,211,102,0.4)' }}>
              📤 Share on WhatsApp
            </button>
            <button className="sc2-share-btn" onClick={handleCopy}
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1.5px solid rgba(255,255,255,0.2)' }}>
              📋 Copy Message
            </button>
            <button onClick={() => setCard(null)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Nunito',sans-serif", padding: 6 }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ShareCardProvider;
